import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, AlertTriangle } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const STATUSES = ["pending", "processing", "processed", "rejected"];
const statusCls = {
  pending: "bg-brand-orange/15 text-brand-orange",
  processing: "bg-blue-500/15 text-blue-400",
  processed: "bg-brand-green/15 text-brand-greenLight",
  rejected: "bg-red-500/15 text-red-400",
};

export default function AdminCashouts() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (type !== "all") params.type = type;
    api.get("/admin/cashouts", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, newStatus) => {
    try { await api.patch(`/admin/cashouts/${id}`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/cashouts/export", { params: status !== "all" ? { status } : {}, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "cashouts.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-cashouts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Cashout management</h1>
          <p className="mt-1 text-muted-foreground">Process payouts. Overdue &gt;24h are flagged.</p>
        </div>
        <Button onClick={exportCsv} data-testid="export-cashouts-button" variant="outline" className="gap-2 rounded-full"><Download className="h-4 w-4" /> Export CSV</Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-40" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{["eft", "card", "ewallet", "airtime", "voucher"].map((t) => <SelectItem key={t} value={t} className="uppercase">{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Worker</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Net</TableHead><TableHead>Requested</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Update</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid="cashout-row" className={c.overdue ? "bg-brand-orange/5" : ""}>
                <TableCell className="font-semibold">{c.worker_name}</TableCell>
                <TableCell><Badge variant="secondary" className="uppercase">{c.type}</Badge></TableCell>
                <TableCell className="text-right font-semibold">R{c.amount.toFixed(2)}</TableCell>
                <TableCell className="text-right text-muted-foreground">R{c.net?.toFixed(2)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">{c.overdue && <AlertTriangle className="h-3.5 w-3.5 text-brand-orange" />}{new Date(c.created_at).toLocaleDateString("en-ZA")}</span>
                </TableCell>
                <TableCell><Badge className={`border-0 capitalize ${statusCls[c.status]}`}>{c.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Select value={c.status} onValueChange={(v) => update(c.id, v)}>
                    <SelectTrigger className="ml-auto w-32" data-testid={`update-status-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No cashouts match these filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
